import React from "react";

import {
  MapContainer,
  Marker,
  TileLayer,
  Popup,
  useMap,
  useMapEvent,
  useMapEvents,
  Tooltip,
  ZoomControl,
} from "react-leaflet";

import L from "leaflet";

import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import "leaflet/dist/leaflet.css";

import osm from "./osm-provider";
import useGeoLocation from "./hook/useGeoLocation";
import LeafletGetCurrentLocation from "./LeafletGetCurrentLocation";
import LeafletGetDirections from "./LeafletGetDirections";

import "./style.css";

import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.js";

import iconLocationMarker from "./assets/location-marker.svg";
import iconDestinationMarker from "./assets/destination-marker.svg";
import LeafletGeocoder from "./LeafletGeocoder";

function MapPlaceholder() {
  return (
    <p>
      Map of Portugal.{" "}
      <noscript>You need to enable JavaScript to see this map.</noscript>
    </p>
  );
}
const LISBON = {
  lat: 38.736946,
  lng: -9.142685,
};

const isInsidePortugal = (lat, lng) => {
  const mainland =
    lat >= 36.8 &&
    lat <= 42.2 &&
    lng >= -9.6 &&
    lng <= -6.0;

  const madeira =
    lat >= 32.5 &&
    lat <= 32.9 &&
    lng >= -17.4 &&
    lng <= -16.5;

  const azores =
    lat >= 36.8 &&
    lat <= 39.9 &&
    lng >= -31.5 &&
    lng <= -24.5;

  return mainland || madeira || azores;
};

function LocationMarker({ position, setPosition, zoom, isDirectionAble, t, onAddressFound }) {
  const DRAGGABLE = true;
  const markerRef = React.useRef(null);
  const map = useMap();

  const eventHandlers = React.useMemo(
    () => ({
      async dragend() {
        const marker = markerRef.current;

        if (!marker) return;

        const newPos = marker.getLatLng();

        if (!isInsidePortugal(newPos.lat, newPos.lng)) {
          marker.setLatLng(LISBON);
          setPosition(LISBON);
          map.flyTo(LISBON, zoom);
          return;
        }

        setPosition(newPos);

        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${newPos.lat}&lon=${newPos.lng}`;

          const res = await fetch(url);
          const data = await res.json();

          if (data.address && onAddressFound) {
            onAddressFound(data.address);
          }
        } catch (error) {
          console.error("Erro no reverse geocoding:", error);
        }
      },
    }),
    [setPosition, map, zoom, onAddressFound]
  );

  React.useEffect(() => {
    if (!position) return;

    if (isInsidePortugal(position.lat, position.lng)) {
      map.flyTo(position, zoom, { animate: true });
    } else {
      map.flyTo(LISBON, zoom, { animate: true });
      setPosition(LISBON);
    }
  }, [position]);

  return position === null ? null : (
    <Marker
      position={
        !isDirectionAble
          ? position
          : sourceAddress?.latitude != null &&
            sourceAddress?.longitude != null
            ? {
              lat: Number(sourceAddress.latitude),
              lng: Number(sourceAddress.longitude),
            }
            : null
      }
      draggable={DRAGGABLE}
      eventHandlers={eventHandlers}
      ref={markerRef}
    >
      <Popup>{t("youAreHere")}</Popup>
      <Tooltip>
        {t("youAreHere")}
        <br />
        <Typography
          variant="subtitle2"
          component="span"
          color="text.secondary"
          fontWeight={"normal"}
          fontSize={"12px"}
        >
          lat : {Number(position.lat).toFixed(4)}, lng:{" "}
          {Number(position.lng).toFixed(4)}
        </Typography>
      </Tooltip>
    </Marker>
  );
}


function DestionationMarker({ position, destinationAddress, t }) {
  const DRAGGABLE = false;
  const markerRef = React.useRef(null);
  const destinationIcon = L.icon({
    iconRetinaUrl: iconDestinationMarker,
    iconUrl: iconDestinationMarker,
    shadowUrl: null,
    iconSize: [35, 35],
    iconAnchor: null,
    popupAnchor: [0, -20],
    tooltipAnchor: [15, 0],
  });

  return position === null ||
    position.lat === undefined ||
    position.lng === undefined ? null : (
    <Marker
      position={position}
      draggable={DRAGGABLE}
      ref={markerRef}
      icon={destinationIcon}
    >
      <Popup>
        {" "}
        <Stack direction={"column"} ml={2}>
          <Typography fontWeight={"normal"} fontSize={"12px"}>
            {destinationAddress?.addressTitle}{" "}
            <Typography
              component={"span"}
              fontWeight={"normal"}
              fontSize={"12px"}
            >
              {destinationAddress?.addressLine}
            </Typography>
            <br />
            <Typography
              fontWeight={"normal"}
              fontSize={"12px"}
              component={"span"}
            >
              {destinationAddress?.district}{" "}
            </Typography>
            <Typography
              fontWeight={"normal"}
              fontSize={"12px"}
              component={"span"}
            >
              {destinationAddress?.county}{" "}
            </Typography>
            <Typography
              fontWeight={"normal"}
              fontSize={"12px"}
              component={"span"}
            >
              {destinationAddress?.subDivision}{" "}
            </Typography>
            <br />
            <Typography
              fontWeight={"normal"}
              fontSize={"12px"}
              component={"span"}
            >
              {destinationAddress?.postalCode}{" "}
            </Typography>
            <Typography
              fontWeight={"normal"}
              fontSize={"12px"}
              component={"span"}
            >
              {destinationAddress?.locality}
            </Typography>
          </Typography>
        </Stack>
      </Popup>
      <Tooltip>
        {/* {t("destinationAddress")} */}
        <br />
        <Typography
          variant="subtitle2"
          component="span"
          color="text.secondary"
          fontWeight={"normal"}
          fontSize={"12px"}
        >
          lat : {Number(position.lat).toFixed(4)}, lng:{" "}
          {Number(position.lng).toFixed(4)}
        </Typography>
      </Tooltip>
    </Marker>
  );
}

const MapLeaflet = ({
  isDirectionAble = false,
  isPin = true,
  isSearchAble = false,
  position,
  setPosition,
  sourceAddress,
  destinationAdrress,
  t,
  onAddressFound,
}) => {
  const [center, setCenter] = React.useState({
    lat: 38.736946,
    lng: -9.142685,
  });

  const BTN_POSITION = "topleft";
  const ZOOM_LEVEL = isDirectionAble ? 10 : 13;
  const isScrollWheelZoom = false;
  const portugalBounds = [
    [36.8, -9.6],   // Sudoeste
    [42.2, -6.0],   // Nordeste
  ];
  const markerBound = React.useMemo(() => {
    if (isDirectionAble && sourceAddress && destinationAdrress) {
      const points = [
        {
          lat: Number(sourceAddress.latitude),
          lng: Number(sourceAddress.longitude),
        },
        {
          lat: Number(destinationAdrress.latitude),
          lng: Number(destinationAdrress.longitude),
        },
      ];

      return points.filter(p =>
        isInsidePortugal(p.lat, p.lng)
      );
    }
    return [];
  }, [isDirectionAble, sourceAddress, destinationAdrress]);

  const mapRef = React.useRef();

  return (
    <MapContainer
      center={center}
      zoom={ZOOM_LEVEL}
      scrollWheelZoom={isScrollWheelZoom}
      style={{ width: "100%", height: "100%" }}
      ref={mapRef}
      placeholder={<MapPlaceholder />}
      attributionControl={false}
      zoomControl={false}
      //bounds={markerBound.length > 0 ? markerBound : undefined}
    >
      <ZoomControl position={BTN_POSITION} />
      <TileLayer
        attribution={osm.mapTiler.attribution}
        url={osm.mapTiler.url}
      />
      {isPin && (
        <LeafletGetCurrentLocation
          btnPosition={BTN_POSITION}
          setPosition={setPosition}
          zoom={ZOOM_LEVEL}
          t={t}
        />
      )}
      <LocationMarker
        position={
          !isDirectionAble
            ? position
            : { lat: sourceAddress?.latitude, lng: sourceAddress?.longitude }
        }
        setPosition={setPosition}
        isDirectionAble={isDirectionAble}
        zoom={ZOOM_LEVEL}
        t={t}
        onAddressFound={onAddressFound}  // <-- use the destructured prop directly
      />
      {isSearchAble && <LeafletGeocoder setPosition={setPosition} />}
      {isDirectionAble && (
        <DestionationMarker
          position={
            destinationAdrress?.latitude != null &&
              destinationAdrress?.longitude != null
              ? {
                lat: Number(destinationAdrress.latitude),
                lng: Number(destinationAdrress.longitude),
              }
              : null
          }
          destinationAddress={destinationAdrress}
          t={t}
        />
      )}
      {isDirectionAble && (
        <LeafletGetDirections
          btnPosition={BTN_POSITION}
          sourceLat={sourceAddress?.latitude}
          sourceLng={sourceAddress?.longitude}
          destLat={destinationAdrress?.latitude}
          destLng={destinationAdrress?.longitude}
          zoom={ZOOM_LEVEL}
          t={t}
        />
      )}
    </MapContainer>
  );
};

export const defaultMarkerIcon = L.icon({
  iconRetinaUrl: iconLocationMarker,
  iconUrl: iconLocationMarker,
  shadowUrl: null,
  iconSize: [35, 35],
  iconAnchor: null,
  popupAnchor: [0, -20],
  tooltipAnchor: [15, 0],
});

L.Marker.prototype.options.icon = defaultMarkerIcon;

export default MapLeaflet;
